import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Plus, Trash2, DollarSign, FileText, Car, Briefcase, Search, Zap, CreditCard, Check, ChevronsUpDown, User, Save, Star, Building2, ChevronLeft, Users } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { cn } from '../../../lib/utils';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';

const BASE_LINE_ITEM_TYPES = [
  { value: 'service', label: 'Process Service', icon: FileText },
  { value: 'rush_fee', label: 'Rush Fee', icon: Zap },
  { value: 'mileage', label: 'Mileage', icon: Car },
  { value: 'filing_fee', label: 'Filing Fee', icon: Briefcase },
  { value: 'skip_trace', label: 'Skip Trace', icon: Search },
  { value: 'copies', label: 'Copies', icon: FileText },
  { value: 'processing_fee', label: 'Processing Fee (%)', icon: CreditCard },
  { value: 'custom', label: 'Custom', icon: DollarSign },
];

// Default descriptions for each line item type
const DEFAULT_DESCRIPTIONS = {
  'service': 'Personal Service',
  'rush_fee': 'Rush Service Fee',
  'mileage': 'Mileage',
  'filing_fee': 'Filing Fee',
  'skip_trace': 'Skip Trace Fee',
  'copies': 'Document Copies',
  'processing_fee': '', // Processing fee uses percentage format
  'custom': '',
  'extra_attempt': 'Additional Attempt Fee'
};

const PAYMENT_TERMS = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
];

export default function InvoiceDialog({ open, onOpenChange, invoice, clients, profile, invoiceSettings, onSave, onClientsChange }) {
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [selectedCompanyView, setSelectedCompanyView] = useState(null); // For drill-down into company contacts
  const [companies, setCompanies] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [lineItems, setLineItems] = useState([]);
  const [newItemId, setNewItemId] = useState(null);
  const lineItemsEndRef = useRef(null);
  const [saveCustomItem, setSaveCustomItem] = useState({});
  
  // Quick Add Company state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddData, setQuickAddData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: ''
  });
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  
  // Load companies when dialog opens
  useEffect(() => {
    if (open) {
      loadCompanies();
    }
  }, [open]);
  
  const loadCompanies = async () => {
    try {
      const response = await api.getInvoiceCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };
  
  // Quick Add Company handler
  const handleQuickAdd = async () => {
    // Require at least a contact name
    if (!quickAddData.contactName.trim()) {
      toast.error('Please enter a contact name');
      return;
    }
    
    setQuickAddSaving(true);
    try {
      let companyId = null;
      
      // Create company if name provided - check for existing first
      if (quickAddData.companyName.trim()) {
        // Check if company already exists with this name
        const existingCompany = companies.find(
          c => c.name.toLowerCase() === quickAddData.companyName.trim().toLowerCase()
        );
        
        if (existingCompany) {
          companyId = existingCompany.id;
        } else {
          const companyResponse = await api.createInvoiceCompany({
            name: quickAddData.companyName.trim()
          });
          companyId = companyResponse.data.id;
        }
      }
      
      // Create contact (email is optional)
      const contactResponse = await api.createInvoiceClient({
        name: quickAddData.contactName.trim(),
        email: quickAddData.contactEmail.trim() || null,
        company_id: companyId || null
      });
      
      // Refresh data
      await loadCompanies();
      if (onClientsChange) {
        await onClientsChange();
      }
      
      // Select the new contact
      setClientId(contactResponse.data.id);
      setClientPopoverOpen(false);
      setShowQuickAdd(false);
      setQuickAddData({ companyName: '', contactName: '', contactEmail: '' });
      
      toast.success(companyId ? 'Company and contact created!' : 'Contact created!');
    } catch (error) {
      console.error('Failed to create:', error);
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setQuickAddSaving(false);
    }
  };
  
  // Group clients by company
  const { companiesWithContacts, standaloneContacts } = useMemo(() => {
    const standalone = clients.filter(c => !c.company_id);
    const byCompany = {};
    
    companies.forEach(company => {
      byCompany[company.id] = {
        company,
        contacts: clients.filter(c => c.company_id === company.id)
      };
    });
    
    return {
      companiesWithContacts: Object.values(byCompany).filter(g => g.contacts.length > 0),
      standaloneContacts: standalone
    };
  }, [clients, companies]);
  
  // Get selected client display name
  const getSelectedClientDisplay = () => {
    if (!clientId) return null;
    const client = clients.find(c => c.id === clientId);
    if (!client) return null;
    
    // If client has a company, show company name prominently
    if (client.company_id) {
      const company = companies.find(c => c.id === client.company_id);
      if (company) {
        return {
          primary: company.name,
          secondary: `${client.name}${client.title ? ` (${client.title})` : ''}`
        };
      }
    }
    
    // Standalone contact
    return {
      primary: client.name,
      secondary: client.company_name || null
    };
  }; // Track which custom items to save
  
  // Build the complete line item types including saved custom items
  const LINE_ITEM_TYPES = useMemo(() => {
    const savedItems = invoiceSettings?.custom_line_items || [];
    const customItems = savedItems.map((item, index) => ({
      value: `saved_custom_${index}`,
      label: item.description,
      icon: Star,
      isSavedCustom: true,
      defaultPrice: item.default_price,
      defaultDescription: item.description
    }));
    return [...BASE_LINE_ITEM_TYPES, ...customItems];
  }, [invoiceSettings?.custom_line_items]);
  
  // Invoice-level optional fields (shown above line items)
  const [entityServed, setEntityServed] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [serviceAddress, setServiceAddress] = useState('');

  // Get custom default prices from invoice settings (or use system defaults)
  const getDefaultPrice = (itemType) => {
    // Check if it's a saved custom item
    if (itemType.startsWith('saved_custom_')) {
      const itemTypeObj = LINE_ITEM_TYPES.find(t => t.value === itemType);
      if (itemTypeObj?.defaultPrice) {
        return itemTypeObj.defaultPrice;
      }
    }
    
    const customPrices = invoiceSettings?.default_prices || {};
    const systemDefaults = {
      'service': 85,
      'rush_fee': 50,
      'mileage': 0.67,
      'filing_fee': 0,
      'skip_trace': 35,
      'copies': 0.10,
      'processing_fee': 3,
      'custom': 0,
      'extra_attempt': 25
    };
    return customPrices[itemType] ?? systemDefaults[itemType] ?? 0;
  };
  
  // Get default description for item type
  const getDefaultDescription = (itemType) => {
    // Check if it's a saved custom item
    if (itemType.startsWith('saved_custom_')) {
      const itemTypeObj = LINE_ITEM_TYPES.find(t => t.value === itemType);
      return itemTypeObj?.defaultDescription || '';
    }
    return DEFAULT_DESCRIPTIONS[itemType] || '';
  };

  useEffect(() => {
    if (open) {
      if (invoice) {
        // Editing existing invoice
        setClientId(invoice.client_id);
        setPaymentTerms(invoice.payment_terms);
        setNotes(invoice.notes || '');
        setTaxRate(invoice.tax_rate || 0);
        setLineItems(invoice.line_items || []);
        setEntityServed(invoice.entity_served || '');
        setCaseNumber(invoice.case_number || '');
        setReferenceNumber(invoice.reference_number || '');
        setServiceDate(invoice.service_date || '');
        setServiceAddress(invoice.service_address || '');
      } else {
        // New invoice - use defaults from settings
        setClientId(clients.length > 0 ? clients[0].id : '');
        setPaymentTerms(invoiceSettings?.default_payment_terms || 'net_30');
        setNotes('');
        setTaxRate(0);
        setLineItems([createEmptyLineItem('service')]);
        setEntityServed('');
        setCaseNumber('');
        setReferenceNumber('');
        setServiceDate('');
        setServiceAddress('');
      }
    }
  }, [open, invoice, clients, invoiceSettings]);

  const createEmptyLineItem = (type = 'service') => {
    const price = getDefaultPrice(type);
    const quantity = type === 'mileage' ? 0 : 1;
    return {
      id: uuidv4(),
      item_type: type,
      description: getDefaultDescription(type),
      quantity: quantity,
      unit_price: price,
      total: type === 'processing_fee' ? 0 : quantity * price
    };
  };

  const addLineItem = () => {
    const newItem = createEmptyLineItem();
    setLineItems([...lineItems, newItem]);
    setNewItemId(newItem.id); // Track for pulse animation
    // Scroll to the new line item after state update
    setTimeout(() => {
      lineItemsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    // Clear animation after it completes
    setTimeout(() => {
      setNewItemId(null);
    }, 1500);
  };

  const removeLineItem = (id) => {
    if (lineItems.length === 1) {
      toast.error('Invoice must have at least one line item');
      return;
    }
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      
      const updated = { ...item, [field]: value };
      
      // When item type changes, auto-populate description and reset unit_price appropriately
      if (field === 'item_type') {
        const oldDefault = getDefaultDescription(item.item_type);
        // If description is empty or matches the old default, update it to the new default
        if (!item.description || item.description === oldDefault) {
          updated.description = getDefaultDescription(value);
        }
        
        // Always reset to the default price for the new item type
        const newPrice = getDefaultPrice(value);
        updated.unit_price = newPrice;
        
        // Set appropriate quantity
        if (value === 'processing_fee') {
          updated.quantity = 1;
        } else if (value === 'mileage') {
          updated.quantity = 0; // User will enter miles
          updated.total = 0;
        } else {
          updated.quantity = 1;
          updated.total = newPrice;
        }
      }
      
      // For processing_fee, unit_price is a percentage - total calculated later
      if (updated.item_type === 'processing_fee') {
        updated.quantity = 1;
        return updated;
      }
      
      // Recalculate total when quantity or unit_price changes
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(item.quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(item.unit_price) || 0;
        updated.total = Math.round(qty * price * 100) / 100;
      }
      
      return updated;
    }));
  };

  // Calculate subtotal excluding processing fees first
  const calculateBaseSubtotal = () => {
    return lineItems
      .filter(item => item.item_type !== 'processing_fee')
      .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const calculateSubtotal = () => {
    const baseSubtotal = calculateBaseSubtotal();
    
    // Calculate processing fees based on base subtotal
    const processingFees = lineItems
      .filter(item => item.item_type === 'processing_fee')
      .reduce((sum, item) => {
        const percent = parseFloat(item.unit_price) || 0;
        return sum + Math.round(baseSubtotal * (percent / 100) * 100) / 100;
      }, 0);
    
    return baseSubtotal + processingFees;
  };

  const calculateTax = () => {
    return Math.round(calculateSubtotal() * (taxRate / 100) * 100) / 100;
  };

  const calculateTotal = () => {
    return Math.round((calculateSubtotal() + calculateTax()) * 100) / 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientId) {
      toast.error('Please select a client');
      return;
    }
    
    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    
    // Description is now optional - no validation needed
    
    setSaving(true);
    try {
      // Calculate base subtotal for processing fee calculations
      const baseSubtotal = lineItems
        .filter(item => item.item_type !== 'processing_fee')
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const payload = {
        payment_terms: paymentTerms,
        // Invoice-level optional fields
        entity_served: entityServed || null,
        case_number: caseNumber || null,
        reference_number: referenceNumber || null,
        service_date: serviceDate || null,
        service_address: serviceAddress || null,
        // Send client's local year for accurate invoice numbering (handles timezone edge cases)
        client_year: new Date().getFullYear(),
        line_items: lineItems.map(item => {
          // For processing fees, calculate the total based on percentage
          if (item.item_type === 'processing_fee') {
            const percent = parseFloat(item.unit_price) || 0;
            const calculatedTotal = Math.round(baseSubtotal * (percent / 100) * 100) / 100;
            return {
              ...item,
              description: item.description || `Processing Fee (${percent}%)`,
              quantity: 1,
              unit_price: parseFloat(item.unit_price) || 0,
              total: calculatedTotal
            };
          }
          return {
            ...item,
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            total: parseFloat(item.total) || 0
          };
        }),
        notes: notes,
        tax_rate: parseFloat(taxRate) || 0
      };
      
      // Only include client_id for new invoices or draft edits
      if (!invoice || invoice.status === 'draft') {
        payload.client_id = clientId;
      }
      
      let savedInvoice = null;
      if (invoice) {
        const resp = await api.updateInvoice(invoice.id, payload);
        savedInvoice = resp.data;
        toast.success('Invoice updated');
      } else {
        const resp = await api.createInvoice(payload);
        savedInvoice = resp.data;
        toast.success('Invoice created');
      }
      
      // Save custom items for future use if checked
      const itemsToSave = lineItems.filter(item => 
        item.item_type === 'custom' && 
        saveCustomItem[item.id] && 
        item.description && 
        item.unit_price > 0
      );
      
      if (itemsToSave.length > 0) {
        try {
          const currentSettings = await api.getInvoiceSettings();
          const existingCustomItems = currentSettings.data.custom_line_items || [];
          
          // Add new items, avoiding duplicates by description
          const newCustomItems = itemsToSave
            .filter(item => !existingCustomItems.some(existing => 
              existing.description.toLowerCase() === item.description.toLowerCase()
            ))
            .map(item => ({
              description: item.description,
              default_price: parseFloat(item.unit_price) || 0
            }));
          
          if (newCustomItems.length > 0) {
            await api.updateInvoiceSettings({
              ...currentSettings.data,
              custom_line_items: [...existingCustomItems, ...newCustomItems]
            });
            toast.success(`${newCustomItems.length} custom item(s) saved for future use`);
          }
        } catch (err) {
          console.error('Failed to save custom items:', err);
        }
      }
      
      // Reset save custom item state
      setSaveCustomItem({});
      
      onSave(savedInvoice);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            {invoice ? (invoice.status === 'paid' ? 'Add Supplemental Charges' : 'Edit Invoice') : 'Create Invoice'}
          </DialogTitle>
        </DialogHeader>

        {/* Supplemental Charges Notice */}
        {invoice && invoice.status === 'paid' && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
            <p className="text-amber-400 text-sm font-medium">Adding Supplemental Charges</p>
            <p className="text-amber-300/70 text-xs mt-1">
              This invoice has been paid. Any new charges you add will create a new balance due.
              The client will be notified and can pay the additional amount.
            </p>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-slate-400">Original Total: <span className="text-white font-medium">{formatCurrency(invoice.total_paid || invoice.total)}</span></span>
              <span className="text-slate-400">Amount Paid: <span className="text-green-400 font-medium">{formatCurrency(invoice.total_paid || 0)}</span></span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client & Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Client *</Label>
              <Popover open={clientPopoverOpen} onOpenChange={(open) => {
                setClientPopoverOpen(open);
                if (!open) setSelectedCompanyView(null);
              }}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={clientPopoverOpen}
                    disabled={invoice && ['paid', 'void'].includes(invoice.status)}
                    className={cn(
                      "w-full justify-between bg-slate-800 border-slate-700 text-white hover:bg-slate-700",
                      !clientId && "text-slate-400",
                      invoice && ['paid', 'void'].includes(invoice.status) && "opacity-50"
                    )}
                    data-testid="invoice-client-select"
                  >
                    <div className="flex flex-col items-start truncate">
                      {(() => {
                        const display = getSelectedClientDisplay();
                        if (!display) return <span>Select client...</span>;
                        return (
                          <>
                            <span className="truncate">{display.primary}</span>
                            {display.secondary && (
                              <span className="text-xs text-slate-400 truncate">{display.secondary}</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0 bg-slate-800 border-slate-700" align="start">
                  <Command className="bg-slate-800">
                    {selectedCompanyView ? (
                      // Drill-down view: Show contacts for selected company
                      <>
                        <div 
                          className="flex items-center gap-2 px-3 py-2 border-b border-slate-700 cursor-pointer hover:bg-slate-700/50"
                          onClick={() => setSelectedCompanyView(null)}
                        >
                          <ChevronLeft className="w-4 h-4 text-slate-400" />
                          <Building2 className="w-4 h-4 text-teal-400" />
                          <span className="text-white font-medium truncate">{selectedCompanyView.company.name}</span>
                        </div>
                        <CommandList className="max-h-[280px] overflow-y-auto" onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; }}>
                          <CommandGroup heading="Contacts">
                            {selectedCompanyView.contacts.map((contact) => (
                              <CommandItem
                                key={contact.id}
                                value={contact.name}
                                onSelect={() => {
                                  setClientId(contact.id);
                                  setClientPopoverOpen(false);
                                  setSelectedCompanyView(null);
                                }}
                                className="text-white data-[selected=true]:bg-slate-700 cursor-pointer"
                              >
                                <Check className={cn("mr-2 h-4 w-4", clientId === contact.id ? "opacity-100 text-teal-400" : "opacity-0")} />
                                <User className="mr-2 h-4 w-4 text-slate-400" />
                                <div className="flex flex-col flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="truncate">{contact.name}</span>
                                    {contact.is_primary && (
                                      <Badge className="bg-teal-500/20 text-teal-400 text-[10px] px-1 py-0">Primary</Badge>
                                    )}
                                  </div>
                                  {contact.title && (
                                    <span className="text-xs text-slate-400 truncate">{contact.title}</span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </>
                    ) : (
                      // Main view: Show companies and standalone contacts
                      <>
                        <CommandInput placeholder="Search..." className="text-white" />
                        <CommandList className="max-h-[280px] overflow-y-auto" onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; }}>
                          <CommandEmpty className="text-slate-400 py-4 text-center">No results found.</CommandEmpty>
                          
                          {companiesWithContacts.length > 0 && (
                            <CommandGroup heading="Companies">
                              {companiesWithContacts.map(({ company, contacts }) => (
                                <CommandItem
                                  key={company.id}
                                  value={company.name}
                                  onSelect={() => setSelectedCompanyView({ company, contacts })}
                                  className="text-white data-[selected=true]:bg-slate-700 cursor-pointer"
                                >
                                  <Building2 className="mr-2 h-4 w-4 text-teal-400" />
                                  <div className="flex flex-col flex-1 min-w-0">
                                    <span className="truncate">{company.name}</span>
                                    <span className="text-xs text-slate-400">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</span>
                                  </div>
                                  <ChevronsUpDown className="h-4 w-4 text-slate-500" />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                          
                          {standaloneContacts.length > 0 && (
                            <>
                              {companiesWithContacts.length > 0 && <CommandSeparator className="bg-slate-700" />}
                              <CommandGroup heading="Standalone Contacts">
                                {standaloneContacts.map((contact) => (
                                  <CommandItem
                                    key={contact.id}
                                    value={`${contact.name} ${contact.company_name || ''}`}
                                    onSelect={() => {
                                      setClientId(contact.id);
                                      setClientPopoverOpen(false);
                                    }}
                                    className="text-white data-[selected=true]:bg-slate-700 cursor-pointer"
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", clientId === contact.id ? "opacity-100 text-teal-400" : "opacity-0")} />
                                    <User className="mr-2 h-4 w-4 text-slate-400" />
                                    <div className="flex flex-col">
                                      <span className="truncate">{contact.name}</span>
                                      {contact.company_name && (
                                        <span className="text-xs text-slate-400 truncate">{contact.company_name}</span>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                          
                          {/* Quick Add Company/Contact Section */}
                          <CommandSeparator className="bg-slate-700" />
                          <CommandGroup>
                            <CommandItem
                              onSelect={() => setShowQuickAdd(true)}
                              className="text-teal-400 data-[selected=true]:bg-slate-700 cursor-pointer"
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              <span>Quick Add Company/Contact</span>
                            </CommandItem>
                          </CommandGroup>
                        </CommandList>
                      </>
                    )}
                  </Command>
                  
                  {/* Quick Add Form - Outside Command for better scrolling */}
                  {showQuickAdd && !selectedCompanyView && (
                    <div className="p-3 space-y-3 bg-slate-800 border-t border-slate-600">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-teal-400 font-medium">Quick Add Client</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowQuickAdd(false);
                            setQuickAddData({ companyName: '', contactName: '', contactEmail: '' });
                          }}
                          className="h-6 px-2 text-slate-400 hover:text-white"
                        >
                          Cancel
                        </Button>
                      </div>
                      <Input
                        placeholder="Company Name (leave blank for standalone contact)"
                        value={quickAddData.companyName}
                        onChange={(e) => setQuickAddData(prev => ({ ...prev, companyName: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white h-8 text-sm"
                        onWheel={(e) => e.stopPropagation()}
                      />
                      <Input
                        placeholder="Contact Name *"
                        value={quickAddData.contactName}
                        onChange={(e) => setQuickAddData(prev => ({ ...prev, contactName: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white h-8 text-sm"
                        onWheel={(e) => e.stopPropagation()}
                      />
                      <Input
                        placeholder="Contact Email *"
                        type="email"
                        value={quickAddData.contactEmail}
                        onChange={(e) => setQuickAddData(prev => ({ ...prev, contactEmail: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white h-8 text-sm"
                        onWheel={(e) => e.stopPropagation()}
                      />
                      <Button
                        onClick={handleQuickAdd}
                        disabled={quickAddSaving || !quickAddData.contactName.trim() || !quickAddData.contactEmail.trim()}
                        className="w-full h-8 bg-teal-600 hover:bg-teal-500 text-white text-sm"
                      >
                        {quickAddSaving ? 'Creating...' : 'Create & Select'}
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              {invoice && ['paid', 'void'].includes(invoice.status) && (
                <p className="text-xs text-slate-500">Client cannot be changed on paid/void invoices</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Payment Terms</Label>
              <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {PAYMENT_TERMS.map(term => (
                    <SelectItem key={term.value} value={term.value} className="text-white">
                      {term.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Optional Reference Fields */}
          <div className="space-y-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
            <Label className="text-slate-400 text-sm">Optional Details (shown on invoice if filled)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Entity Served</Label>
                <Input
                  value={entityServed}
                  onChange={(e) => setEntityServed(e.target.value)}
                  placeholder="John Doe, ABC Corp, etc."
                  className="bg-slate-800 border-slate-700 text-white text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Case #</Label>
                <Input
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                  placeholder="CV-2026-1234"
                  className="bg-slate-800 border-slate-700 text-white text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Reference # (Client's internal #)</Label>
                <Input
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="Client reference"
                  className="bg-slate-800 border-slate-700 text-white text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Service Date</Label>
                <Input
                  type="date"
                  value={serviceDate}
                  onChange={(e) => setServiceDate(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Line Items</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={addLineItem}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="add-line-item-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </Button>
            </div>
            
            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <div 
                  key={item.id} 
                  className={`p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3 transition-all duration-300 ${
                    newItemId === item.id ? 'ring-2 ring-cyan-500 ring-opacity-75 animate-pulse bg-slate-700/50' : ''
                  }`}
                  data-testid={`line-item-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <Select 
                      value={item.item_type} 
                      onValueChange={(v) => updateLineItem(item.id, 'item_type', v)}
                    >
                      <SelectTrigger className="w-52 bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 max-h-64">
                        {BASE_LINE_ITEM_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value} className="text-white">
                            {type.label}
                          </SelectItem>
                        ))}
                        {invoiceSettings?.custom_line_items?.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs text-slate-500 border-t border-slate-700 mt-1">
                              Saved Custom Items
                            </div>
                            {invoiceSettings.custom_line_items.map((savedItem, idx) => (
                              <SelectItem 
                                key={`saved_custom_${idx}`} 
                                value={`saved_custom_${idx}`} 
                                className="text-amber-300"
                              >
                                <span className="flex items-center gap-2">
                                  <Star className="h-3 w-3" />
                                  {savedItem.description}
                                </span>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeLineItem(item.id)}
                      className="text-slate-400 hover:text-red-400 hover:bg-slate-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Description - optional for all types */}
                  <div className="space-y-2">
                    <Input
                      value={item.description}
                      onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                      placeholder="Description (optional)"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                    {/* Save for future use checkbox - only for custom items */}
                    {item.item_type === 'custom' && item.description && item.unit_price > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox
                          id={`save-custom-${item.id}`}
                          checked={saveCustomItem[item.id] || false}
                          onCheckedChange={(checked) => {
                            setSaveCustomItem(prev => ({...prev, [item.id]: checked}));
                          }}
                          className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                        />
                        <label 
                          htmlFor={`save-custom-${item.id}`}
                          className="text-xs text-slate-400 cursor-pointer flex items-center gap-1"
                        >
                          <Save className="h-3 w-3" />
                          Save for future use
                        </label>
                      </div>
                    )}
                  </div>
                  
                  {/* Processing Fee - percentage based */}
                  {item.item_type === 'processing_fee' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-400">Percentage</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            min="0"
                            max="10"
                            step="0.1"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(item.id, 'unit_price', e.target.value)}
                            className="pr-7 bg-slate-700 border-slate-600 text-white"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-400">Calculated Amount</Label>
                        <Input
                          value={formatCurrency(calculateBaseSubtotal() * (parseFloat(item.unit_price) || 0) / 100)}
                          disabled
                          className="bg-slate-800 border-slate-600 text-white font-medium"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-400">Quantity</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-400">Unit Price</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(item.id, 'unit_price', e.target.value)}
                            className="pl-7 bg-slate-700 border-slate-600 text-white"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-400">Total</Label>
                        <Input
                          value={formatCurrency(item.total)}
                          disabled
                          className="bg-slate-800 border-slate-600 text-white font-medium"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* Scroll target for new line items */}
              <div ref={lineItemsEndRef} />
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2 bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Subtotal</span>
                <span className="text-white">{formatCurrency(calculateSubtotal())}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">Tax</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="w-16 h-7 text-xs bg-slate-700 border-slate-600 text-white"
                  />
                  <span className="text-slate-400">%</span>
                </div>
                <span className="text-white">{formatCurrency(calculateTax())}</span>
              </div>
              <div className="border-t border-slate-700 pt-2 flex justify-between">
                <span className="text-white font-medium">Total</span>
                <span className="text-white font-bold text-lg">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-slate-300">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment instructions, terms, or other notes..."
              className="bg-slate-800 border-slate-700 text-white"
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="save-invoice-btn"
            >
              {saving ? 'Saving...' : (invoice ? 'Update Invoice' : 'Create Invoice')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
